import {
    Calendar,
    ChevronDown,
    ChevronUp,
    Circle,
    CircleAlert,
    CircleCheck,
    Lightbulb,
    Upload
} from 'lucide-react';
import { observer } from 'mobx-react';
import * as React from 'react';
import { Badge } from '@components/ui/badge';
import { Card, CardContent } from '@components/ui/card';
import { cn } from '@core/utils/cn';
import { injectTSDI } from '@core/tsdi';
import { ChallengeStore, StepStatus } from './store';

export function getStepIcon(status: StepStatus) {
    switch (status) {
        case StepStatus.CREATING:
            return <Calendar className="size-6 text-info" />;
        case StepStatus.DISABLED:
            return <Calendar className="size-6 text-muted-foreground" />;
        case StepStatus.ERROR:
            return <CircleAlert className="size-6 text-destructive" />;
        case StepStatus.INCOMPLETE:
            return <Circle className="size-6 text-warning" />;
        case StepStatus.MODIFIED:
            return <Lightbulb className="size-6 text-warning" />;
        case StepStatus.UPDATING:
            return <Upload className="size-6 text-success" />;
        case StepStatus.COMPLETED:
            return <CircleCheck className="size-6 text-success" />;
    }
}

const statusVariants = {
    [StepStatus.CREATING]: 'info',
    [StepStatus.DISABLED]: 'muted',
    [StepStatus.ERROR]: 'destructive',
    [StepStatus.INCOMPLETE]: 'warning',
    [StepStatus.MODIFIED]: 'warning',
    [StepStatus.UPDATING]: 'success',
    [StepStatus.COMPLETED]: 'success'
} as const;

export function getStatusBadge(status?: StepStatus) {
    if (!status) {
        return null;
    }
    return (
        <Badge variant={statusVariants[status]}>
            {translateChallengeStepStatus(status)}
        </Badge>
    );
}

export function translateChallengeStepStatus(status: StepStatus) {
    switch (status) {
        case StepStatus.CREATING:
            return 'Creating';
        case StepStatus.DISABLED:
            return 'Disabled';
        case StepStatus.ERROR:
            return 'Invalid';
        case StepStatus.INCOMPLETE:
            return 'Incomplete';
        case StepStatus.MODIFIED:
            return 'Modified';
        case StepStatus.UPDATING:
            return 'Updating';
        case StepStatus.COMPLETED:
            return 'Complete';
    }
}

export const ChallengeFormSteps = observer(() => {
    const { stepsMap, onStepToggle } = injectTSDI(ChallengeStore);

    return (
        <div className="flex flex-col gap-4">
            {stepsMap.map(({ name, Content, status, isActive, key }) => (
                <Card
                    key={name}
                    className={cn(
                        'gap-0 py-0',
                        status === StepStatus.ERROR && 'border-destructive'
                    )}
                >
                    <button
                        type="button"
                        className="flex w-full items-center justify-between gap-4 px-6 py-4 text-left"
                        onClick={() => onStepToggle(key)}
                    >
                        <div className="flex items-center gap-3">
                            {getStepIcon(status)}
                            <span className="font-semibold">{name}</span>
                            {status === StepStatus.MODIFIED && (
                                <span className="text-sm text-muted-foreground italic">
                                    Unsaved changes
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            {getStatusBadge(status)}
                            {isActive ? (
                                <ChevronUp className="size-4 text-muted-foreground" />
                            ) : (
                                <ChevronDown className="size-4 text-muted-foreground" />
                            )}
                        </div>
                    </button>
                    {isActive && (
                        <CardContent className="border-t pt-6 pb-6">
                            <Content />
                        </CardContent>
                    )}
                </Card>
            ))}
        </div>
    );
});

export default ChallengeFormSteps;
